import type { ReactNode } from 'react';
import { ArrowLeft, ChevronRight, Plus } from 'lucide-react';
import {
  buildPortalHelpOverlayRoute,
  clearPortalHelpOverlayRoute,
  getPortalHelpOverlayArticleId,
} from '@/lib/portal-help';
import {
  cn,
  DescriptionText,
  HorizontalStack,
  PropertyGrid,
  PropertyItem,
  Surface,
  VerticalStack,
  type CommerceAdminSectionId,
  type CommerceSummaryMetricViewModel,
  type CatalogEditorFormValue,
  type CommerceShellViewModel,
  type ProductEditorFormValue,
  type VariantEditorFormValue,
} from '@workspace/silk-ui';

import type { Catalog, Product, ProductOption, ProductVariant, ProductVariantOffer } from '@/lib/commerce';
import { buildCatalogDirectoryRoute } from '@/lib/commerce-routing';
import { validateVariantAttributes, variantAttributesToForm } from './variantAttributes';
import { minorCurrencyToMajorNumber } from '@/features/commerce-admin/currency';

export type EditorMode = 'create' | 'edit';
export type ValidationMap = Record<string, string>;
export const equipmentLeaseRecurringOfferType = 'equipment_lease_recurring';
export const productBrowseVisibilityOptions = [
  { value: 'hidden', label: 'Hidden' },
  { value: 'public', label: 'Public' },
] as const;

export function formatRelativeTime(value?: string | null): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

export function metadataRecordToEntries(record?: Record<string, unknown> | null) {
  return Object.entries(record || {}).map(([key, value]) => ({
    id: key,
    key,
    value: String(value),
    valueType: 'string' as const,
  }));
}

export function metadataEntriesToRecord(
  entries: Array<{ key: string; value: string }> | undefined
): Record<string, unknown> {
  return (entries || []).reduce<Record<string, unknown>>((acc, entry) => {
    const key = entry.key.trim();
    if (!key) {
      return acc;
    }
    acc[key] = entry.value;
    return acc;
  }, {});
}

export function emptyCatalogForm(): CatalogEditorFormValue {
  return {
    identity: {
      catalogKey: '',
      name: '',
    },
    lifecycle: {
      status: 'draft',
      defaultCurrency: 'USD',
    },
    description: '',
    metadata: [],
  };
}

export function catalogToForm(catalog: Catalog): CatalogEditorFormValue {
  return {
    identity: {
      catalogKey: catalog.catalog_key,
      name: catalog.name,
    },
    lifecycle: {
      status: catalog.status,
      defaultCurrency: catalog.default_currency || 'USD',
    },
    description: catalog.description || '',
    metadata: metadataRecordToEntries(catalog.metadata),
  };
}

export function emptyProductForm(): ProductEditorFormValue {
  return {
    identity: {
      productKey: '',
      name: '',
      shortDescription: '',
      description: '',
    },
    lifecycle: {
      status: 'draft',
      publishStatus: 'unpublished',
      browseVisibility: 'hidden',
      sortOrder: 0,
    },
    classification: {
      productTypeId: '',
      serviceClass: '',
      serviceGroup: '',
      facets: [],
      flags: [],
    },
    defaultVariantId: '',
    metadata: [],
  };
}

export function productToForm(product: Product): ProductEditorFormValue {
  return {
    identity: {
      productKey: product.product_key,
      name: product.name,
      shortDescription: product.short_description || '',
      description: product.description || '',
    },
    lifecycle: {
      status: product.status,
      publishStatus: product.publish_status,
      browseVisibility: product.browse_visibility,
      sortOrder: product.sort_order,
    },
    classification: {
      productTypeId: product.product_type_key || '',
      serviceClass: product.service_class || '',
      serviceGroup: product.service_group || '',
      facets: (product.facets || []).map(item => ({
        id: item.classification_id,
        key: item.classification_key,
        value: item.classification_value,
        sortOrder: item.sort_order,
      })),
      flags: (product.flags || []).map(item => ({
        id: item.classification_id,
        key: item.classification_key,
        value: item.classification_value,
        sortOrder: item.sort_order,
      })),
    },
    defaultVariantId: product.default_variant_id || '',
    metadata: metadataRecordToEntries(product.metadata),
  };
}

export function emptyVariantForm(): VariantEditorFormValue {
  return {
    specifications: [],
    identity: {
      variantKey: '',
      name: '',
      displayName: '',
    },
    lifecycle: {
      status: 'draft',
      sellable: false,
      sortOrder: 0,
    },
    fulfillment: {
      installationPolicy: 'not_required',
      fulfillmentCode: '',
      inventoryPolicy: '',
    },
    attributes: [],
    optionValueIds: [],
    metadata: [],
  };
}

export function variantToForm(variant: ProductVariant): VariantEditorFormValue {
  return {
    specifications: (variant.specifications || []).map((row, index) => ({ ...row, group: row.group || '', id: `specification-${index}` })),
    identity: {
      variantKey: variant.variant_key,
      name: variant.name,
      displayName: variant.display_name || '',
    },
    lifecycle: {
      status: variant.status,
      sellable: variant.sellable,
      sortOrder: variant.sort_order,
    },
    fulfillment: {
      installationPolicy: variant.installation_required ? 'required' : 'not_required',
      fulfillmentCode: variant.fulfillment_code || '',
      inventoryPolicy: variant.inventory_policy || '',
    },
    attributes: variantAttributesToForm(variant.attributes),
    optionValueIds: variant.option_value_ids || [],
    metadata: metadataRecordToEntries(variant.metadata),
  };
}

export function emptyOfferForm() {
  return {
    identity: {
      offerKey: '',
      name: '',
      displayName: '',
    },
    lifecycle: {
      status: 'draft',
      sellable: false,
      isDefault: false,
      sortOrder: 0,
    },
    commercial: {
      offerType: 'subscription',
      billingPeriodMonths: null,
      termMonths: null,
      fulfillmentCode: '',
      leaseTerms: {
        purchaseCostAmount: null,
        purchaseCostCurrency: 'USD',
        leaseRateBps: null,
        allowedTermMonthsText: '',
        minimumUpfrontPaymentAmount: null,
        maximumUpfrontPaymentAmount: null,
      },
    },
    metadata: [],
  };
}

function offerLeaseTermsSource(offer: ProductVariantOffer) {
  return offer.lease_terms || {
    purchase_cost_amount_minor: offer.purchase_cost_amount_minor,
    purchase_cost_currency: offer.purchase_cost_currency,
    lease_rate_bps: offer.lease_rate_bps,
    allowed_term_months: offer.allowed_term_months,
    minimum_upfront_payment_amount_minor: offer.minimum_upfront_payment_amount_minor,
    maximum_upfront_payment_amount_minor: offer.maximum_upfront_payment_amount_minor,
  };
}

export function formatAllowedTermMonthsText(value?: number[] | null): string {
  return (value || []).join(', ');
}

export function parseAllowedTermMonthsText(value: string): number[] {
  return value
    .split(',')
    .map(part => Number.parseInt(part.trim(), 10))
    .filter(term => Number.isFinite(term) && term > 0);
}

export function offerToForm(offer: ProductVariantOffer) {
  const leaseTerms = offerLeaseTermsSource(offer);
  return {
    identity: {
      offerKey: offer.offer_key,
      name: offer.name,
      displayName: offer.display_name || '',
    },
    lifecycle: {
      status: offer.status,
      sellable: offer.sellable,
      isDefault: offer.is_default,
      sortOrder: offer.sort_order,
    },
    commercial: {
      offerType: offer.offer_type,
      billingPeriodMonths: offer.billing_period_months ?? null,
      billingUnit: offer.billing_period_months && offer.billing_period_months % 12 === 0 ? 'years' as const : 'months' as const,
      termUnit: offer.term_months && offer.term_months % 12 === 0 ? 'years' as const : 'months' as const,
      termMonths: offer.term_months ?? null,
      fulfillmentCode: offer.fulfillment_code || '',
      leaseTerms: {
        purchaseCostAmount: minorCurrencyToMajorNumber(
          leaseTerms.purchase_cost_amount_minor,
          leaseTerms.purchase_cost_currency || 'USD',
        ),
        purchaseCostCurrency: leaseTerms.purchase_cost_currency || 'USD',
        leaseRateBps: leaseTerms.lease_rate_bps ?? null,
        allowedTermMonthsText: formatAllowedTermMonthsText(leaseTerms.allowed_term_months),
        minimumUpfrontPaymentAmount: minorCurrencyToMajorNumber(
          leaseTerms.minimum_upfront_payment_amount_minor,
          leaseTerms.purchase_cost_currency || 'USD',
        ),
        maximumUpfrontPaymentAmount: minorCurrencyToMajorNumber(
          leaseTerms.maximum_upfront_payment_amount_minor,
          leaseTerms.purchase_cost_currency || 'USD',
        ),
      },
    },
    metadata: metadataRecordToEntries(offer.metadata),
  };
}

export function validateCatalogForm(form: CatalogEditorFormValue): ValidationMap {
  const errors: ValidationMap = {};
  if (!form.identity.catalogKey.trim()) {
    errors.catalogKey = 'Catalog key is required.';
  }
  if (!form.identity.name.trim()) {
    errors.name = 'Catalog name is required.';
  }
  return errors;
}

export function validateProductForm(form: ProductEditorFormValue): ValidationMap {
  const errors: ValidationMap = {};
  if (!form.identity.name.trim()) {
    errors.name = 'Product name is required.';
  }
  if (!Number.isFinite(form.lifecycle.sortOrder)) {
    errors.sortOrder = 'Sort order must be a number.';
  }
  return errors;
}

export function validateVariantForm(
  form: VariantEditorFormValue,
  attributeDefinitions: Parameters<typeof validateVariantAttributes>[1] = [],
  validateAttributes = true,
): ValidationMap {
  const errors: ValidationMap = {};
  if (!form.identity.name.trim()) {
    errors.name = 'Variant name is required.';
  }
  if (!Number.isFinite(form.lifecycle.sortOrder)) {
    errors.sortOrder = 'Sort order must be a number.';
  }
  return validateAttributes
    ? { ...errors, ...validateVariantAttributes(form.attributes, attributeDefinitions) }
    : errors;
}

export function validateOfferForm(form: ReturnType<typeof emptyOfferForm>): ValidationMap {
  const errors: ValidationMap = {};
  if (!form.identity.name.trim()) {
    errors.name = 'Offer name is required.';
  }
  if (!form.commercial.offerType.trim()) {
    errors.offerType = 'Offer type is required.';
  }
  if (
    form.commercial.termMonths !== null &&
    form.commercial.termMonths !== undefined &&
    (!Number.isSafeInteger(form.commercial.termMonths) || form.commercial.termMonths <= 0)
  ) {
    errors.termMonths = 'Contract duration must be a positive whole number of months, or no fixed term.';
  }
  const period = form.commercial.billingPeriodMonths;
  const recurring = !['purchase', 'customer_owned'].includes(form.commercial.offerType);
  if (recurring && form.lifecycle.status === 'active' && period == null) {
    errors.billingPeriodMonths = 'Choose a billing frequency before activating this offer.';
  }
  if (period != null && (!Number.isSafeInteger(period) || period <= 0)) {
    errors.billingPeriodMonths = 'Billing frequency must be a positive whole number of months.';
  }
  if (period && form.commercial.termMonths && form.commercial.termMonths % period !== 0) {
    errors.termMonths = 'Contract duration must contain whole billing periods.';
  }
  if (!Number.isFinite(form.lifecycle.sortOrder)) {
    errors.sortOrder = 'Sort order must be a number.';
  }
  return errors;
}

export function flattenOptionValueOptions(product: Product | null) {
  return (product?.options || []).flatMap(option =>
    option.values.map(value => ({
      value: value.option_value_id,
      label: value.display_value || value.value,
      description: option.display_name || option.name,
    }))
  );
}

export function optionSummaryLabel(option: ProductOption): string {
  return option.display_name || option.name;
}

export function buildCommerceShellViewModel(args: {
  activeTenant: { id?: string | null; name?: string | null } | null | undefined;
  accessDeniedStatus: number | null;
  activeSectionId?: CommerceAdminSectionId;
  searchQuery: string;
  searchPlaceholder?: string;
  onSearchChange: (value: string) => void;
  error: string | null;
  blockingReason?: string | null;
  emptyStateLabel?: string;
  onSelectSection: (sectionId: CommerceAdminSectionId) => void;
  primaryAction?: { id: string; label: string; onAction?: () => void; disabled?: boolean };
  includeHelpAction?: boolean;
  metrics?: CommerceSummaryMetricViewModel[];
}): CommerceShellViewModel {
  const {
    activeTenant,
    accessDeniedStatus,
    activeSectionId = 'catalogs',
    searchQuery,
    searchPlaceholder,
    onSearchChange,
    error,
    blockingReason,
    emptyStateLabel,
    onSelectSection,
    primaryAction,
    includeHelpAction = true,
    metrics,
  } =
    args;

  const sections: CommerceShellViewModel['sections'] = [
    { id: 'catalogs', label: 'Catalogs', description: 'Product library' },
    { id: 'storefronts', label: 'Storefronts', description: 'Runtime setup' },
    { id: 'markets', label: 'Markets', description: 'Geo rollout and segments' },
    { id: 'access_points', label: 'Access Points', description: 'Fiber route anchors' },
    { id: 'reseller_entitlements', label: 'Entitlements', description: 'Sharing and selling rights' },
    { id: 'linked_resellers', label: 'Linked Resellers', description: 'Tenant visibility directory' },
    { id: 'linked_sellers', label: 'Linked Sellers', description: 'Approved seller directory' },
    { id: 'pricebooks', label: 'Pricebooks', description: 'Pricing rules' },
    { id: 'commission_plans', label: 'Commissions', description: 'Agent payouts' },
    { id: 'commission_ledger', label: 'Commission Ledger', description: 'Payout reporting' },
    { id: 'assignments', label: 'Assignments', description: 'Direct targeting' },
    { id: 'discounts', label: 'Discounts', description: 'Promotions' },
    { id: 'carts', label: 'Carts', description: 'Active sessions' },
    { id: 'quotes', label: 'Quotes', description: 'Sales pipeline' },
    { id: 'orders', label: 'Orders', description: 'Fulfillment' },
  ];

  return {
    ownerTenant: {
      tenantId: activeTenant?.id || null,
      tenantLabel: activeTenant?.name || 'No Tenant Selected',
      emptyStateLabel: emptyStateLabel || 'Select a tenant to manage catalogs.',
    },
    access: {
      catalogsManageGranted: true,
      pricebooksManageGranted: true,
      accessEnabled: !accessDeniedStatus,
      blockingReason: accessDeniedStatus ? blockingReason || 'Your current role does not have permission to manage this commerce workspace.' : null,
    },
    activeSectionId,
    sections,
    toolbar: {
      showSearch: false,
      searchValue: searchQuery,
      searchPlaceholder: searchPlaceholder || 'Search the current commerce level...',
      onSearchChange,
      secondaryAction:
        includeHelpAction
          ? {
              id: `${activeSectionId}-help`,
              label: 'Help',
              tone: 'neutral',
              href: buildCommerceHelpOverlayRoute(activeSectionId),
            }
          : undefined,
      primaryAction: primaryAction
        ? {
            id: primaryAction.id,
            label: primaryAction.label,
            tone: 'primary',
            onAction: primaryAction.onAction,
            disabled: primaryAction.disabled,
          }
        : undefined,
    },
    metrics,
    callouts: error
      ? [{ id: 'catalog-error', title: 'Catalog Workspace Error', body: error, tone: 'danger' }]
      : [],
    onSelectSection: sectionId => onSelectSection(sectionId),
  };
}

export interface BreadcrumbItem {
  label: string;
  onSelect?: () => void;
}

export function CommerceBreadcrumbHeader({
  breadcrumbs,
  title,
  description,
  onBack,
}: {
  breadcrumbs: BreadcrumbItem[];
  title: string;
  description?: string;
  onBack?: () => void;
}) {
  const hasParent = breadcrumbs.length > 1;

  return (
    <HorizontalStack gap={3} className="items-center min-w-0">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-background transition-all hover:bg-muted/50 hover:border-border active:scale-95"
          aria-label="Go back"
        >
          <ArrowLeft className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-x-0.5" />
        </button>
      )}

      <VerticalStack gap={1} className="min-w-0">
        {/* Breadcrumb trail — only shown when there is a parent */}
        {hasParent && (
          <HorizontalStack gap={1} className="text-xs text-muted-foreground/70 flex-wrap">
            {breadcrumbs.slice(0, -1).map((crumb, index) => (
              <HorizontalStack key={`${crumb.label}-${index}`} gap={1} className="min-w-0 items-center">
                {index > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground/40" />}
                {crumb.onSelect ? (
                  <button
                    type="button"
                    onClick={crumb.onSelect}
                    className="truncate font-semibold hover:text-foreground transition-colors"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="truncate font-semibold">{crumb.label}</span>
                )}
              </HorizontalStack>
            ))}
          </HorizontalStack>
        )}

        {/* Main title */}
        <h1 className={cn(
          "font-bold tracking-tight text-foreground font-heading leading-none truncate",
          hasParent ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"
        )}>
          {title}
        </h1>

        {/* Description */}
        {description && (
          <DescriptionText className="hidden sm:block text-xs sm:text-sm mt-0.5 max-w-2xl leading-relaxed">
            {description}
          </DescriptionText>
        )}
      </VerticalStack>
    </HorizontalStack>
  );
}

export function CommerceSummarySurface({
  title,
  metrics,
  body,
}: {
  title: string;
  metrics: Array<{ label: string; value: string }>;
  body?: string;
}) {
  return (
    <Surface className="border-border/60 p-5">
      <VerticalStack gap={4}>
        <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">
          {title}
        </p>
        <PropertyGrid cols={3}>
          {metrics.map(metric => (
            <PropertyItem key={metric.label} label={metric.label} value={metric.value} mono={false} />
          ))}
        </PropertyGrid>
        {body ? <DescriptionText className="text-xs">{body}</DescriptionText> : null}
      </VerticalStack>
    </Surface>
  );
}

export function CommerceRegistryUtilityBand({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('border-b border-border/40 bg-muted/5 px-3 py-3 sm:px-6', className)}>
      {children}
    </div>
  );
}

export function buildCommerceSectionRoute(sectionId: string) {
  if (sectionId === 'catalogs') {
    return buildCatalogDirectoryRoute();
  }
  if (sectionId === 'storefronts') {
    return '/commerce/storefronts';
  }
  if (sectionId === 'publications') {
    return '/commerce/publications';
  }
  if (sectionId === 'markets') {
    return '/commerce/markets';
  }
  if (sectionId === 'access_points') {
    return '/commerce/access-points';
  }
  if (sectionId === 'channels' || sectionId === 'eligibility') {
    return '/commerce/storefronts';
  }
  if (sectionId === 'reseller_entitlements') {
    return '/commerce/entitlements';
  }
  if (sectionId === 'linked_resellers') {
    return '/commerce/linked-resellers';
  }
  if (sectionId === 'linked_sellers') {
    return '/reseller/linked-sellers';
  }
  if (sectionId === 'pricebooks') {
    return '/commerce/pricebooks';
  }
  if (sectionId === 'assignments') {
    return '/commerce/assignments';
  }
  if (sectionId === 'commission_plans') {
    return '/commerce/commission-plans';
  }
  if (sectionId === 'commission_ledger') {
    return '/commerce/commission-ledger';
  }
  if (sectionId === 'discounts') {
    return '/commerce/discounts';
  }
  if (sectionId === 'carts') {
    return '/commerce/carts';
  }
  if (sectionId === 'quotes') {
    return '/commerce/quotes';
  }
  if (sectionId === 'orders') {
    return '/commerce/orders';
  }
  return buildCatalogDirectoryRoute();
}

export function getCommerceHelpOverlaySection(path: string | null | undefined) {
  const articleId = getPortalHelpOverlayArticleId(path);
  const prefix = 'commerce-';
  if (!articleId?.startsWith(prefix)) {
    return null;
  }
  return articleId.slice(prefix.length);
}

export function buildCommerceHelpOverlayRoute(sectionId: string, path?: string | null) {
  const currentPath = path || (typeof window === 'undefined' ? buildCommerceSectionRoute(sectionId) : `${window.location.pathname}${window.location.search}`);
  return buildPortalHelpOverlayRoute(`commerce-${sectionId}`, currentPath);
}

export function clearCommerceHelpOverlayRoute(path: string | null | undefined) {
  return clearPortalHelpOverlayRoute(path);
}
